'use client'

import {
  Badge,
  Box,
  Flex,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { ViewOnSiteButton } from '../../../../components/ViewOnSiteButton'
import { useHasRole, useAppToast, usePendingRows } from '../../../../helpers'
import { ICoursesInPerson } from '../../../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { CoursesService } from '../../../../services/CoursesService/CoursesService'
import CoursesModal from './CoursesModal'

export default function CoursesPage() {
  const service = new CoursesService()
  const toast = useAppToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const hasRole = useHasRole()
  const permissions = {
    addCourse: hasRole('add_course'),
    editCourse: hasRole('edit_course'),
    toggleCourse: hasRole('toggle_course'),
    deleteCourse: hasRole('delete_course')
  }

  const [length, setLength] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [courses, setCourses] = useState<ICoursesInPerson[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [deleting, setDeleting] = useState<ICoursesInPerson | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const [editing, setEditing] = useState<ICoursesInPerson | undefined>(undefined)

  const loadCourses = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const { data, total_filtered } = await service.list(search, page, length)
      setCourses(data)
      setTotalPages(Math.ceil(total_filtered / length))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os cursos', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCourses()
  }, [])

  useEffect(() => {
    loadCourses()
  }, [length, page])

  useEffect(() => {
    // A busca já chega debounced do `FilterSearch`.
    loadCourses()
  }, [search])

  const handleDelete = async () => {
    if (!deleting) return

    try {
      await service.delete(deleting.id)

      toast.success('Excluído com sucesso', `O curso ${deleting.name} foi removido`)

      setCourses((prev) => prev.filter((item) => item.id !== deleting.id))
    } catch (error) {
      toast.error('Erro ao excluir', 'Não foi possível excluir o curso.')
    } finally {
      setIsDeleteOpen(false)
      setDeleting(null)
    }
  }

  const savingRow = usePendingRows()

  const toggleStatus = async (course: ICoursesInPerson, enabled: boolean) => {
    try {
      await service.updateEnable(course.id, enabled)

      toast.success(`O curso foi atualizado com sucesso`)

      setCourses((prev) => prev.map((item) => (item.id === course.id ? { ...item, enabled } : item)))
    } catch (error) {
      toast.error('Não foi possível atualizar o curso')
    }
  }

  const toggleFeatured = async (course: ICoursesInPerson, featured: boolean) => {
    try {
      await service.updateFeatured(course.id, featured)

      toast.success(`O curso foi atualizado com sucesso`)

      setCourses((prev) => prev.map((item) => (item.id === course.id ? { ...item, is_featured: featured } : item)))
    } catch (error) {
      toast.error('Não foi possível atualizar o curso')
    }
  }

  const toggleShowOnHomepage = async (course: ICoursesInPerson, showOnHomepage: boolean) => {
    try {
      await service.updateShowOnHomepage(course.id, showOnHomepage)

      toast.success(`O curso foi atualizado com sucesso`)

      setCourses((prev) =>
        prev.map((item) => (item.id === course.id ? { ...item, show_on_homepage: showOnHomepage } : item))
      )
    } catch (error) {
      toast.error('Não foi possível atualizar o curso')
    }
  }

  return (
    <>
      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch value={search} onChange={setSearch} placeholder="Buscar por curso" />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome do curso</Th>
              <Th>Formatos disponíveis</Th>
              <Th>Áreas de Conhecimento</Th>
              <Th>Dinâmica do Curso</Th>
              <Th>Configurações</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : loadError ? (
              <TableErrorRow colSpan={6} entity="cursos" onRetry={loadCourses} />
            ) : courses.length === 0 ? (
              <TableEmptyRow colSpan={6} entity="curso" />
            ) : (
              courses.map((course) => (
                <Tr key={course.id}>
                  <Td>{course.name}</Td>
                  <Td>
                    <Flex direction="row" gap="2">
                      {course.in_person && <Badge colorScheme="orange">Presencial</Badge>}
                      {course.live_online && <Badge colorScheme="green">Online ao vivo</Badge>}
                    </Flex>
                  </Td>
                  <Td>{course.knowledge_areas?.map((a) => <p key={a.id}>{a.name}</p>) || '-'}</Td>
                  <Td>{course.dynamics?.map((d) => d.name).join(', ') || '-'}</Td>
                  <Td>
                    <Flex direction="column" gap="2" align="flex-start">
                      <Switch
                        isChecked={course.enabled}
                        onChange={(e) => savingRow.run(course.id, () => toggleStatus(course, e.target.checked))}
                        isDisabled={!permissions.toggleCourse || savingRow.isPending(course.id)}
                        display="flex"
                        alignItems="center"
                      >
                        <Box as="span" ml="2" fontSize="xs" fontWeight="normal">
                          Ativo
                        </Box>
                      </Switch>
                      <Switch
                        isChecked={course.is_featured}
                        onChange={(e) => savingRow.run(course.id, () => toggleFeatured(course, e.target.checked))}
                        isDisabled={!permissions.toggleCourse || savingRow.isPending(course.id)}
                        display="flex"
                        alignItems="center"
                      >
                        <Box as="span" ml="2" fontSize="xs" fontWeight="normal">
                          Mais procurado
                        </Box>
                      </Switch>
                      <Switch
                        isChecked={course.show_on_homepage}
                        onChange={(e) => savingRow.run(course.id, () => toggleShowOnHomepage(course, e.target.checked))}
                        isDisabled={!permissions.toggleCourse || savingRow.isPending(course.id)}
                        display="flex"
                        alignItems="center"
                      >
                        <Box as="span" ml="2" fontSize="xs" fontWeight="normal">
                          Mostrar na home
                        </Box>
                      </Switch>
                    </Flex>
                  </Td>
                  <Td>
                    {course.course_link && <ViewOnSiteButton href={course.course_link} />}
                    {permissions.editCourse && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setEditing(course)
                          onOpen()
                        }}
                      />
                    )}
                    {permissions.deleteCourse && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeleting(course)
                          setIsDeleteOpen(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={length}
        onPageSizeChange={(pageSize) => {
          setLength(pageSize)
          setPage(1)
        }}
      />

      {permissions.addCourse && (
        <ActionFab
          label="Adicionar curso"
          onClick={() => {
            onOpen()
          }}
        />
      )}

      <CoursesModal
        onSuccess={loadCourses}
        editCourse={editing}
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setEditing(undefined)
        }}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        action="delete"
        entity="curso"
        name={deleting?.name}
      />
    </>
  )
}
